import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ModeEditOutlineTwoTone, DeleteTwoTone } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';

import AppTable from 'components/Shared/AppTable';
import FormDrawer from 'components/Shared/FormDrawer';
import BreadcrumbLayout from 'layouts/BreadcrumbLayout';
import { useShipmentDetailContext } from 'contexts/ShipmentDetailContext';
import { useAuth, useTablePagination } from 'hooks';
import { useUpdateShipmentMutation } from 'store/services/shipmentApi';
import ShipmentItemsForm from './ShipmentItemsForm';

const ShipmentItemsList = () => {
  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const canEdit = isCustomer || isVendor || isContractor || isDriver;
  const [showItemForm, setShowItemForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const { ShipmentDetail } = useShipmentDetailContext();
  const [updateShipment, { isLoading: updatingShipment }] = useUpdateShipmentMutation();

  const columns = [
    {
      name: 'Item Name',
      selector: (row) => row.name || '',
      cell: (row) => row.name || 'N/A',
      sortable: true,
    },
     {
      name: 'Item Reference',
      selector: (row) => row.itemReference || '',
    },
     {
      name: 'Cargo Type',
      selector: (row) => row.cargoType?.name || '',
    },
     {
      name: 'Cargo Condition',
      selector: (row) => row.cargoCondition?.name || '',
    },
    {
      name: 'Weight (kg)',
      selector: (row) => row.weight || 0,
      cell: (row) => row.weight ? row.weight.toFixed(2) : 'N/A',
      sortable: true,
    },
    {
      name: 'Dimensions (L x W x H)',
      selector: (row) => row.dimensions?.length || 0,
      cell: (row) =>
        row.dimensions
          ? `${row.dimensions.length?.toFixed(2) || 0} x ${row.dimensions.width?.toFixed(2) || 0} x ${row.dimensions.height?.toFixed(2) || 0} m`
          : 'N/A',
      sortable: true,
    },
    (!canEdit ? [
       {
      name: 'Actions',
      cell: (row, index) => (
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              aria-label="Edit Item"
              onClick={() => {
                setSelectedItem({ ...row, index });
                setShowItemForm(true);
              }}
            >
              <ModeEditOutlineTwoTone />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Delete Item"
              color="error"
              onClick={async () => {
                try {
                  const updatedDetails = ShipmentDetail.shipmentDetails.filter(
                    (_, i) => i !== index
                  );
                  console.log('Deleting item, updating shipment with:', {
                    _id: ShipmentDetail._id,
                    shipmentDetails: updatedDetails,
                  });
                  await updateShipment({
                    _id: ShipmentDetail._id,
                    shipmentDetails: updatedDetails,
                  }).unwrap();
                  toast.success('Item deleted successfully!');
                } catch (err) {
                  console.error('Delete item error:', err);
                  toast.error(err?.data?.message || 'Failed to delete item!');
                }
              }}
            >
              <DeleteTwoTone />
            </IconButton>
          </Box>
      ),
      ignoreRowClick: true,
      right: true,
    },
    ] : [])
   
  ];

  const sortedItems = useMemo(() => {
    const items = Array.isArray(ShipmentDetail?.shipmentDetails)
      ? ShipmentDetail.shipmentDetails
      : [];
    // If createdAt exists, sort by createdAt in descending order
    if (items.length > 0 && items[0]?.createdAt) {
      return [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    // Otherwise, reverse the array for LIFO (last added item first)
    return [...items].reverse();
  }, [ShipmentDetail?.shipmentDetails]);

  const handleCancelItem = () => {
    setShowItemForm(false);
    setSelectedItem(null);
  };

  const handleSubmitItem = (updatedDetails) => {
    setShowItemForm(false);
    setSelectedItem(null);
  };

  return (
    <div>
      <BreadcrumbLayout.Paper>
        <AppTable
          columns={columns}
          data={sortedItems}
          progressPending={false}
          progressComponent={
            <Box display="flex" alignItems="center" padding={2}>
              <CircularProgress size={24} />
              <Typography variant="body2" marginLeft={1}>
                Loading Shipment Items...
              </Typography>
            </Box>
          }
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={sortedItems.length}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          noDataComponent={
            <Typography variant="body2" padding={2}>
              No shipment items found.
            </Typography>
          }
        />
      </BreadcrumbLayout.Paper>
      <FormDrawer
        title={selectedItem ? 'Edit Item' : 'Add Item'}
        open={showItemForm}
        onClose={handleCancelItem}
      >
        <ShipmentItemsForm
          isBusy={updatingShipment}
          item={selectedItem}
          shipmentId={ShipmentDetail?._id}
          onSubmit={handleSubmitItem}
          onCancel={handleCancelItem}
        />
      </FormDrawer>
    </div>
  );
};

export default ShipmentItemsList;